import PropTypes from "prop-types";

// material-ui
import { useTheme } from "@mui/material/styles";
import { Box, Drawer, useMediaQuery } from "@mui/material";

// third-party
import { BrowserView, MobileView } from "react-device-detect";

// project imports
import MenuList from "./MenuList";
import LogoSection from "../LogoSection";
import { drawerTopOffset, drawerWidth } from "../../../store/constant";
import { useSelector } from "react-redux";
import type { RootState } from "../../../store/reducer";
import { type ReactElement } from "react";

// ==============================|| SIDEBAR DRAWER ||============================== //

interface SidebarProps {
  drawerToggle: () => void;
}

const Sidebar = ({ drawerToggle }: SidebarProps): ReactElement => {
  const theme = useTheme();
  const matchDownMd = useMediaQuery(theme.breakpoints.down("md"));

  const desktopOpen = useSelector((state: RootState) => state.customization.desktopOpen) as boolean;
  const mobileOpen = useSelector((state: RootState) => state.customization.mobileOpen) as boolean;

  const drawer = (
    <>
      <Box sx={{ display: { xs: "block", md: "none" } }}>
        <Box sx={{ display: "flex", p: 2, mx: "auto" }}>
          <LogoSection />
        </Box>
      </Box>

      <BrowserView>
        <Box
          style={{
            height: `calc(100vh - ${drawerTopOffset}px)`,
            paddingLeft: "16px",
            paddingRight: "16px"
          }}
        >
          <MenuList />
        </Box>
      </BrowserView>

      <MobileView>
        <Box sx={{ px: 2 }}>
          <MenuList />
        </Box>
      </MobileView>
    </>
  );

  const container = window !== undefined ? () => window.document.body : undefined;

  return (
    <Box
      component="nav"
      sx={{
        flexShrink: { md: 0 },
        width: matchDownMd ? "auto" : drawerWidth
      }}
      aria-label="mailbox folders"
    >
      <Drawer
        container={container}
        variant={matchDownMd ? "temporary" : "persistent"}
        anchor="left"
        open={matchDownMd ? mobileOpen : desktopOpen}
        onClose={drawerToggle}
        sx={{
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            background: theme.palette.background.default,
            color: theme.palette.text.primary,
            borderRight: "none",
            [theme.breakpoints.up("md")]: {
              top: `${drawerTopOffset}px`
            }
          }
        }}
        ModalProps={{ keepMounted: true }}
        color="inherit"
      >
        {drawer}
      </Drawer>
    </Box>
  );
};

Sidebar.propTypes = {
  mobileOpen: PropTypes.bool,
  desktopOpen: PropTypes.bool,
  drawerToggle: PropTypes.func,
  window: PropTypes.object
};

export default Sidebar;
